const IMG = '/img/review/other';

/** May 1 → Aug 9, 2026 (varied days, not the same early date on every app). */
const RANGE_START = Date.UTC(2026, 4, 1, 0, 0, 0);
const SPAN_DAYS = 100;

const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Random unique days from May 1 → Aug 9 (stable per seed, uneven gaps).
 * Each seed gets a different May “oldest” day so apps do not all show the same date.
 */
export const reviewDatesForCount = (count, seed = 1) => {
  const n = Math.max(1, Number(count) || 1);
  const rand = mulberry32(0x9e3779b9 ^ (seed >>> 0));
  // Distinct May day per seed (May 2–May 28) so oldest dates differ across apps
  const mayOldest = 1 + (((seed * 11) ^ Math.floor(rand() * 17)) % 27);
  const pool = Array.from({ length: SPAN_DAYS + 1 }, (_, i) => i).filter(
    (d) => d !== mayOldest && d >= mayOldest
  );

  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const swap = pool[i];
    pool[i] = pool[j];
    pool[j] = swap;
  }

  const picked = [mayOldest, ...pool.slice(0, Math.max(0, n - 1))];

  return picked.map((dayOffset) => {
    const date = new Date(RANGE_START + dayOffset * 86400000);
    date.setUTCHours(8 + Math.floor(rand() * 12), Math.floor(rand() * 60), 0, 0);
    return date.toISOString();
  });
};

export const reviewCommittedAt = (index, total = 1, seed = 1) =>
  reviewDatesForCount(total, seed)[Math.max(0, Number(index) || 0)];

/**
 * HireProof: unique dates from May 9 → Aug 11, 2026 (always includes both ends).
 */
export const hireProofReviewDates = (count, seed = 77) => {
  const n = Math.max(2, Number(count) || 2);
  const start = Date.UTC(2026, 4, 9, 0, 0, 0); // May 9
  const end = Date.UTC(2026, 7, 11, 0, 0, 0); // Aug 11
  const spanDays = Math.round((end - start) / 86400000); // 94
  const rand = mulberry32(0xc0ffee ^ (seed >>> 0));

  const middle = Array.from({ length: spanDays - 1 }, (_, i) => i + 1);
  for (let i = middle.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const swap = middle[i];
    middle[i] = middle[j];
    middle[j] = swap;
  }

  const offsets = [0, ...middle.slice(0, n - 2), spanDays].slice(0, n);
  // Keep unique if n is small
  const unique = [...new Set(offsets)];
  while (unique.length < n) {
    const extra = 1 + Math.floor(rand() * (spanDays - 1));
    if (!unique.includes(extra)) unique.push(extra);
  }

  return unique.slice(0, n).map((dayOffset) => {
    const date = new Date(start + dayOffset * 86400000);
    date.setUTCHours(8 + Math.floor(rand() * 12), Math.floor(rand() * 60), 0, 0);
    return date.toISOString();
  });
};

export const hireProofCommittedAt = (index, total = 11, seed = 77) =>
  hireProofReviewDates(total, seed)[Math.max(0, Number(index) || 0)];

const stampDates = (list, seed) => {
  const dates = reviewDatesForCount(list.length, seed);
  return list.map((item, index) => ({
    ...item,
    createdAt: dates[index],
  }));
};

const review = (file, name, role, comment, style = 'short') => ({
  id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  name,
  role,
  image: `${IMG}/${file}`,
  comment,
  style,
});

/** Unique developer photos from img/review/other — never reused across apps. */
export const DEVELOPER_APP_REVIEWS = {
  cofounderhubs: stampDates([
    review(
      'Adel Ghabboun.jpeg',
      'Adel Ghabboun',
      'Software Developer',
      'I installed CofounderHubs to stop bouncing between job boards. The desktop app lets me browse startup roles, open a company page, and message founders without losing context. For a developer looking for a smaller team, that flow is actually useful.',
      'long'
    ),
    review(
      'Deval Parikh.jpeg',
      'Deval Parikh',
      'Full-Stack Developer',
      'I use it when I want founder-led roles instead of generic listings, and the profiles help me decide who is worth messaging.',
      'short'
    ),
    review(
      'Eugene McGrath.jpeg',
      'Eugene McGrath',
      'Software Engineer',
      'Cleaner than generic career sites when I am hunting startup engineering roles.',
      'short'
    ),
    review(
      'Evan Brodie.jpeg',
      'Evan Brodie',
      'Backend Developer',
      'Jobs and founder profiles sit in one place, so I can check the role and the team before I apply.',
      'short'
    ),
    review(
      'Evan Tedesco.jpeg',
      'Evan Tedesco',
      'Frontend Developer',
      'Good overview of who is hiring, with enough company context to skip weak fits quickly.',
      'short'
    ),
    review(
      'Feken Baboyan.jpeg',
      'Feken Baboyan',
      'Software Developer',
      'Focused on founders and candidates, which is what I needed.',
      'short'
    ),
    review(
      'Heena Khichadiya.jpeg',
      'Heena Khichadiya',
      'Software Engineer',
      'I keep CofounderHubs installed so new startup roles show up in one feed. The founder-connection angle is more useful than a plain job list, and I can check openings quickly after work.',
      'long'
    ),
  ], 11),
  cursor: stampDates([
    review(
      'Hitesh Sharma.jpeg',
      'Hitesh Sharma',
      'Software Developer',
      'Cursor keeps AI in the editor, so I write and refactor without switching tools. Tab complete is useful, chat is useful, and I still review every diff before I commit.',
      'long'
    ),
    review(
      'Iryna K.jpeg',
      'Iryna K',
      'Full-Stack Developer',
      'It is my daily editor now for feature work, refactors, and quick fixes across the stack.',
      'short'
    ),
    review(
      'Isha Shenolikar.jpeg',
      'Isha Shenolikar',
      'Software Engineer',
      'Fast pair-programming, and I still own the final code.',
      'short'
    ),
    review(
      'Iuliia Kralina.jpeg',
      'Iuliia Kralina',
      'Frontend Developer',
      'I ask for the component change in chat, then review the diff the same way I would review a PR.',
      'short'
    ),
    review(
      'Jahnavi Poptani.jpeg',
      'Jahnavi Poptani',
      'Software Developer',
      'Setup on Windows was smooth, and the AI suggestions stay useful without taking over the file.',
      'short'
    ),
    review(
      'Jeffrey Brown.jpeg',
      'Jeffrey Brown',
      'Backend Developer',
      'Strong for services, tests, and API patches.',
      'short'
    ),
    review(
      'Kasandra Darwin.jpeg',
      'Kasandra Darwin',
      'Software Engineer',
      'Replaced my previous editor for most coding. Setup was easy on macOS and Linux, and the AI help is practical instead of noisy.',
      'long'
    ),
  ], 22),
  kiro: stampDates([
    review(
      'Kiflay Teweldebrhane.jpeg',
      'Kiflay Teweldebrhane',
      'Software Developer',
      'Kiro is useful when a task needs a spec first. I can keep requirements, coding, and delivery in one agentic workflow instead of copying notes between docs and an editor.',
      'long'
    ),
    review(
      'Kitty JEKOVA.jpeg',
      'Kitty Jekova',
      'Software Engineer',
      'Going from a written spec to working code in the same IDE cut a lot of back-and-forth for me.',
      'short'
    ),
    review(
      'Kysielle Lopera.jpeg',
      'Kysielle Lopera',
      'Full-Stack Developer',
      'Saves time on both frontend and backend handoff.',
      'short'
    ),
    review(
      'Leonardo Alexandre.jpeg',
      'Leonardo Alexandre',
      'Software Developer',
      'I start from the spec and stay in the same IDE through implementation, which keeps delivery cleaner.',
      'short'
    ),
    review(
      'Martyn Janes.jpeg',
      'Martyn Janes',
      'Backend Developer',
      'Keeps delivery work organized from requirements through the first working build.',
      'short'
    ),
    review(
      'Nadia TALBI.jpeg',
      'Nadia Talbi',
      'Software Engineer',
      'Practical for real projects, not just demos.',
      'short'
    ),
    review(
      'Natalie Piguliak.jpeg',
      'Natalie Piguliak',
      'Frontend Developer',
      'When UI work needs a written spec, Kiro keeps design notes and implementation together. That is better for team handoff than a pile of chat threads.',
      'long'
    ),
  ], 33),
  vscode: stampDates([
    review(
      'Nicolai Petcov.jpeg',
      'Nicolai Petcov',
      'Software Developer',
      'VS Code is still my default editor. Extensions, Git, and the terminal stay in one window, so collaborating on a product codebase is straightforward.',
      'long'
    ),
    review(
      'Olaoluwa Fatolu.png',
      'Olaoluwa Fatolu',
      'Software Engineer',
      'I install it on every machine because extensions and settings sync well across my setup.',
      'short'
    ),
    review(
      'Oluwakemi Atoyebi.jpeg',
      'Oluwakemi Atoyebi',
      'Full-Stack Developer',
      'Editor, terminal, and extensions cover my stack.',
      'short'
    ),
    review(
      'Pallavi Bhomle.jpeg',
      'Pallavi Bhomle',
      'Software Developer',
      'It is the first editor I install on a new machine, and the extension ecosystem covers almost everything I need.',
      'short'
    ),
    review(
      'Pamella Soares.jpeg',
      'Pamella Soares',
      'Frontend Developer',
      'Shared settings make it easy for the team to keep the same lint and format setup.',
      'short'
    ),
    review(
      'Priyanka Khadilkar.jpeg',
      'Priyanka Khadilkar',
      'Software Engineer',
      'Familiar, stable, good for reviews and debugging.',
      'short'
    ),
    review(
      'Rima Nivale.jpeg',
      'Rima Nivale',
      'Backend Developer',
      'Handles backend projects well. Integrated Git plus the terminal means I can ship services without leaving the editor for basic work.',
      'long'
    ),
  ], 44),
  claude: stampDates([
    review(
      'Romina Ackermann.jpeg',
      'Romina Ackermann',
      'Software Developer',
      'Claude Desktop is useful before I write code. I draft the approach, list edge cases, then move to the editor. It also helps when I need a clear explanation for a teammate.',
      'long'
    ),
    review(
      'Ryan Mcmorris.jpeg',
      'Ryan Mcmorris',
      'Software Engineer',
      'I use it for architecture notes and tradeoff lists before I open a pull request.',
      'short'
    ),
    review(
      'Salam Omarbagaev.jpeg',
      'Salam Omarbagaev',
      'Full-Stack Developer',
      'Plan APIs and draft docs without extra tabs.',
      'short'
    ),
    review(
      'Shweta Pillai.jpeg',
      'Shweta Pillai',
      'Software Developer',
      'Coding questions get structured answers quickly, which helps when I am stuck on an unfamiliar API.',
      'short'
    ),
    review(
      'Stephen Hila.jpeg',
      'Stephen Hila',
      'Backend Developer',
      'Better than scattered notes when I need a clear plan for a service change.',
      'short'
    ),
    review(
      'Thiago Mesquita.jpeg',
      'Thiago Mesquita',
      'Software Engineer',
      'Helpful for coding notes and technical updates.',
      'short'
    ),
    review(
      'Valerii Y.jpeg',
      'Valerii Y',
      'Frontend Developer',
      'I use Claude beside the editor when a UI change needs a written plan. It is good for explaining the change, not just generating markup.',
      'long'
    ),
    review(
      'Yiğit Gürsoy.jpeg',
      'Yiğit Gürsoy',
      'Software Developer',
      'I think through the problem, draft the approach here, then implement in the editor with a clearer plan.',
      'short'
    ),
  ], 55),
};
